import React, { useCallback, useEffect, useState } from 'react'
import { Maybe, Reference, useFetchAllReferencesLazyQuery } from '../../../generated'
import { Autocomplete, TextField } from '@mui/material'
import { useDebounce } from 'use-debounce'

interface ReferenceDropdownProps {
  label?: string
  required?: boolean
  disabled?: boolean
  sx?: object
  customOnChange?: (option: Maybe<Reference>) => void
}
const ReferenceDropdown = ({ label, required, disabled, sx, customOnChange }: ReferenceDropdownProps) => {
  const [references, setReferences] = useState<Maybe<Reference>[]>([])
  const [referencePage, setReferencePage] = useState<number>(1)
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedReference, setSelectedReference] = useState<Maybe<Reference>>(null)

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  const [fetchAllReferences, { loading }] = useFetchAllReferencesLazyQuery({
    onError() {
      setReferences([])
      setHasMorePages(false)
    },
    onCompleted: data => {
      const { fetchAllReferences } = data || {}
      const { references, pagination } = fetchAllReferences || {}
      const { page: currentPageNo, totalPages } = pagination || {}

      if (references && references?.length > 0) {
        setReferences(prevRefs => {
          const updatedRefs = referencePage === 1 ? references : [...(prevRefs || []), ...references]
          return updatedRefs
        })
        const newHasMore = currentPageNo && totalPages ? currentPageNo < totalPages : false
        setHasMorePages(newHasMore)
      } else setHasMorePages(false)
    }
  })

  useEffect(() => {
    setReferences([])
    setReferencePage(1)
    setHasMorePages(true)
  }, [debouncedSearchTerm])

  useEffect(() => {
    const searchValue = debouncedSearchTerm.trim()

    hasMorePages &&
      fetchAllReferences({
        variables: {
          fetchAllReferences: {
            paginationOptions: {
              limit: 20,
              page: referencePage
            },
            active: true,
            keyword: searchValue
          }
        }
      })
  }, [referencePage, debouncedSearchTerm, fetchAllReferences, hasMorePages])

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
        hasMorePages &&
        !loading
      ) {
        setReferencePage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  const handleInputChange = useCallback((event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setSearchTerm(newInputValue)
    }
  }, [])

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: Maybe<Reference> | null) => {
      if (newValue) {
        customOnChange?.(newValue)
        setSelectedReference(newValue)
      } else {
        setSelectedReference(null)
        setSearchTerm('')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  return (
    <Autocomplete
      options={references}
      getOptionLabel={option => option?.referral || ''}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(event, value: Reference | null, reason, details) => {
        handleChange(event, value as Maybe<Reference> | null)
      }}
      onInputChange={handleInputChange}
      value={selectedReference as Reference | null}
      renderInput={params => <TextField {...params} label={label} fullWidth required={required} />}
      ListboxProps={{
        role: 'list-box',
        style: {
          maxHeight: '190px'
        },
        onScroll: handleScroll
      }}
      sx={sx ?? { width: '100%' }}
      noOptionsText={loading ? 'Loading...' : references?.length === 0 ? 'No References' : 'No options'}
      loading={loading}
      freeSolo={false}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      disableCloseOnSelect={false}
      clearText="Clear"
      clearOnEscape
      disableClearable={false}
    />
  )
}

export default ReferenceDropdown
